import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SYRINGES = [30, 50, 100];
const fmt = (n, d = 2) => (Number.isFinite(n) ? Number(n.toFixed(d)).toString() : "—");

function Toggle({ options, value, onChange, testId }) {
  return (
    <div className="flex rounded-md border border-border p-0.5">
      {options.map((o) => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)} data-testid={`${testId}-${o.v}`}
          className={`flex-1 rounded px-3 py-1.5 font-mono-data text-xs transition-colors ${value === o.v ? "bg-emerald-500/15 text-emerald-300" : "text-muted-foreground hover:text-foreground"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SyringeBar({ units, capacity }) {
  const pct = Math.min(100, (units / capacity) * 100);
  return (
    <div className="relative h-6 overflow-hidden rounded-sm border border-border bg-background" data-testid="dose-syringe-bar">
      <div className="h-full bg-emerald-500/40 transition-[width] duration-300" style={{ width: `${pct}%` }} />
      <div className="absolute inset-0 flex justify-between px-px">
        {Array.from({ length: 11 }).map((_, i) => <span key={i} className={`w-px bg-border ${i % 5 === 0 ? "h-full" : "h-2"}`} />)}
      </div>
    </div>
  );
}

const Result = ({ label, value, sub, testId }) => (
  <div className="rounded-md border border-border bg-background/60 p-4">
    <p className="eyebrow">{label}</p>
    <p className="mt-1 font-mono-data text-2xl text-emerald-400" data-testid={testId}>{value}</p>
    {sub && <p className="mt-0.5 font-mono-data text-xs text-muted-foreground">{sub}</p>}
  </div>
);

export function DosingCalculator({ initial = {} }) {
  const [f, setF] = useState({ mg: initial.mg ?? "", ml: initial.ml ?? "", dose: "", unit: "mcg", syringe: 100 });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const mg = Number(f.mg), ml = Number(f.ml), d = Number(f.dose);
  const doseMg = f.unit === "mcg" ? d / 1000 : d;
  const conc = mg > 0 && ml > 0 ? mg / ml : 0;
  const drawMl = conc && doseMg > 0 ? doseMg / conc : 0;
  const units = drawMl * 100;
  return (
    <div className="space-y-6" data-testid="dosing-calculator">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Peptide in the vial (mg)</Label><Input type="number" step="0.1" min="0" value={f.mg} onChange={set("mg")} placeholder="e.g. 10" className="font-mono-data" data-testid="dose-vial-mg-input" /></div>
        <div className="space-y-1.5"><Label>BAC water added (mL)</Label><Input type="number" step="0.1" min="0" value={f.ml} onChange={set("ml")} placeholder="e.g. 2" className="font-mono-data" data-testid="dose-bac-ml-input" /></div>
        <div className="space-y-1.5">
          <Label>Target amount per draw</Label>
          <div className="flex gap-2">
            <Input type="number" step="any" min="0" value={f.dose} onChange={set("dose")} placeholder={f.unit === "mcg" ? "e.g. 250" : "e.g. 2.5"} className="font-mono-data" data-testid="dose-amount-input" />
            <div className="w-32"><Toggle options={[{ v: "mcg", label: "mcg" }, { v: "mg", label: "mg" }]} value={f.unit} onChange={(v) => setF({ ...f, unit: v })} testId="dose-unit" /></div>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Syringe (U-100 insulin)</Label>
          <Toggle options={SYRINGES.map((u) => ({ v: u, label: `${u / 100} mL` }))} value={f.syringe} onChange={(v) => setF({ ...f, syringe: v })} testId="dose-syringe" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Result label="Draw to" value={units ? `${fmt(units, 1)} units` : "—"} sub={drawMl ? `${fmt(drawMl, 3)} mL` : "Fill in all fields"} testId="dose-result-units" />
        <Result label="Concentration" value={conc ? `${fmt(conc)} mg/mL` : "—"} sub={conc ? `${fmt(conc * 100, 1)} mcg per 10 units` : ""} testId="dose-result-concentration" />
        <Result label="Draws per vial" value={conc && doseMg > 0 ? `≈ ${Math.floor(mg / doseMg + 1e-9)}` : "—"} testId="dose-result-doses" />
      </div>
      {units > 0 && (
        <div className="space-y-2">
          <SyringeBar units={units} capacity={f.syringe} />
          <div className="flex justify-between font-mono-data text-[11px] text-muted-foreground"><span>0</span><span>{f.syringe / 2}</span><span>{f.syringe} units</span></div>
          {units > f.syringe && <p className="text-sm text-amber-400" data-testid="dose-warning-over">That's more than one full {f.syringe / 100} mL syringe.</p>}
          {units < 2 && <p className="text-sm text-amber-400" data-testid="dose-warning-small">Under 2 units is hard to measure accurately.</p>}
        </div>
      )}
      <p className="text-xs text-muted-foreground" data-testid="calculator-disclaimer">Calculation aid for laboratory reconstitution only. For research use only. Not for human consumption.</p>
    </div>
  );
}
